import React, {
  BaseSyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Tab } from "react-bootstrap";
import { Formik, Field, FormikProps, FieldProps } from "formik";
import { Form, Nav, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { mainSettingsSchema } from "../../validation/mainSettings";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useUserQuery } from "../../../../graphql/menu.hooks";
import {
  useUpdatePageMutation,
  usePageNewMutation,
  usePageLazyQuery,
  useUpdatePageSettingsMutation,
  PageQuery,
} from "../../../../graphql/page.hooks";
import useGetUrlLocale from "../../../../hooks/useGetUrlLocale";
import {
  PageCategoryQuery,
  usePageCategoryQuery,
} from "../../../../graphql/pageCategory.hooks";
import { Error, FREE_LIMIT } from "../../../../utils";
import { GraphPagePageCategoryInput } from "../../../../../graphql/types";
import { MultiValue } from "react-select";
import ReactSelect from "../../../../components/inputs/select";
import { SelectOption } from "../../../../types";
import {
  isHaveBeenSaved,
  isShowTemplateModal,
  isUpgradeShow,
  isUrlUniqueError,
} from "../../../../graphql/state";
import { useReactiveVar } from "@apollo/client";
import PopoverBtn from "../../../../components/inputs/popoverBtn";
import { StyledInput } from "../../../../components/inputs/styledInput";
import { useGetOptions } from "../../../../hooks/useGetOptions";
import { RestrictionsTab } from "../../components/restrictionsTab/RestrictionsTab";
import crown_img from "../../images/crown.svg";
import { LocationAccessFormHandle } from "../locationAccessForm";
import { ScheduleAccessFormHandle } from "../scheduleAccessForm";
import { PagePasswordFormRef } from "../pagePasswordForm";
import { InfoCircle } from "react-bootstrap-icons";

enum ACTIVE_FORM {
  main = "mainSettings",
  restrictions = "restrictions",
  seo = "seoSettings",
  script = "scriptSettings",
}
enum LENGTH {
  TITLE_SEO = 60,
  SEO_DESCRIPTION = 160,
  KEY = 60,
}

export type PageCategoryItem = PageCategoryQuery["pageCategory"][number];
export type PagePageCategoryItem =
  PageQuery["page"]["pagePageCategory"][number];
interface MainFormProps {
  isEdit?: boolean;
}

interface IValues {
  title: string;
  folderId: string;
  domainId: string | number;
  url: string;
  seoTitle: string;
  seoDescription: string;
  seoKeyWords: string;
  pageCategoryIds: number[];
  noIndex: boolean;
  pageScript: string;
}

export const MainForm: React.FC<MainFormProps> = ({ isEdit }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");
  const folderId = queryParams.get("parentFolder");
  const currentProjectId = queryParams.get("projectId");

  const params = new URLSearchParams(window.location.search);
  const shared = params.has("Shared");

  const { t } = useTranslation();
  const { id } = useParams();
  const lng = useGetUrlLocale();
  const navigate = useNavigate();
  const {
    data: userData,
    loading: userLoading,
    refetch: userRefetch,
  } = useUserQuery();
  const isPremium = userData?.user.activePremium;

  const [fetchPage, { data: page, refetch }] = usePageLazyQuery({
    variables: { id: +id },
    fetchPolicy: "network-only",
  });
  const { data: pageCategoriesData, loading: pageCategoryLoading } =
    usePageCategoryQuery();

  const [updatePage, { loading: updateLoading }] = useUpdatePageMutation();
  const [createPage, { loading: createLoading }] = usePageNewMutation();
  const [updateSettings, { loading: updatingSettingsLoading }] =
    useUpdatePageSettingsMutation();
  const [activeForm, setActiveForm] = useState<string>(ACTIVE_FORM.main);
  const [projectId, setProjectId] = useState<number | string>("");
  const [folders, setFolders] = useState(undefined);
  const [parentFolder, setParentFolder] = useState(undefined);
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
  const [removedCategoryIds, setRemovedCategoryIds] = useState<number[]>([]);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [lines, setLines] = useState<number[]>([1]);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const formikRef = useRef<FormikProps<IValues>>(null);
  const locationFormRef = useRef<LocationAccessFormHandle>(null);
  const scheduleFormRef = useRef<ScheduleAccessFormHandle>(null);
  const passwordFormRef = useRef<PagePasswordFormRef>(null);

  const { domainOptions, folderOptions, accessLoading } = useGetOptions({
    projectId: +projectId,
    setFolders,
  });

  const user = userData ? userData.user : undefined;
  const isPremiumUser = userData?.user?.activePremium || userData?.user?.admin;

  const [showUrlError, setShowUrlError] = useState(false);
  const isUrlError = useReactiveVar(isUrlUniqueError);

  const SETTINGS_TABS = [
    {
      key: ACTIVE_FORM.main,
      text: t("page.main"),
      mobileText: t("page.mainShort"),
      isPremiumTab: false,
    },
    {
      key: ACTIVE_FORM.restrictions,
      text: t("page.Restrictions"),
      mobileText: null,
      isPremiumTab: true,
    },
    {
      key: ACTIVE_FORM.seo,
      text: t("page.seo"),
      mobileText: t("page.seoShort"),
      isPremiumTab: true,
    },
    {
      key: ACTIVE_FORM.script,
      text: t("page.Script"),
      mobileText: null,
      isPremiumTab: false,
    },
  ];

  useEffect(() => {
    if (!pageCategoriesData) return;
    setCategoryOptions(
      pageCategoriesData.pageCategory.map((item: PageCategoryItem) => ({
        value: item.id,
        label: item.title,
      }))
    );
  }, [pageCategoriesData]);

  useEffect(() => {
    userRefetch();
  });

  useEffect(() => {
    if (id) {
      fetchPage();
    }
  }, [id, fetchPage]);

  useEffect(() => {
    if (!folders || !folderId) return;
    const folder = folders.find(
      (folder: { id: number }) => folder.id === +folderId
    );
    setParentFolder(folder);
  }, [folderId, folders]);

  useEffect(() => {
    if (page && isEdit) {
      setHasAccess(
        page?.page?.accessPageFolderDomainByPriority === 2 &&
          userData?.user?.admin
      );
      setProjectId(page?.page?.project.id);
    }
  }, [page, userData?.user, isEdit]);

  useEffect(() => {
    if (!isEdit && currentProjectId) {
      setProjectId(currentProjectId);
    }
  }, [isEdit, currentProjectId]);

  function getWrappedLineCountViaDOM(
    text: string,
    containerWidth: number,
    textareaStyles: Partial<CSSStyleDeclaration> = {}
  ): number {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.visibility = "hidden";
    div.style.whiteSpace = "pre-wrap";
    div.style.wordWrap = "break-word";
    div.style.width = `${containerWidth}px`;

    div.style.fontFamily = textareaStyles.fontFamily || "sans-serif";
    div.style.fontSize = textareaStyles.fontSize || "14px";
    div.style.lineHeight = textareaStyles.lineHeight || "1.5";
    div.style.padding = textareaStyles.padding || "0px";

    div.textContent = text || " ";

    document.body.appendChild(div);

    const computed = getComputedStyle(div);
    const lineHeight = parseFloat(computed.lineHeight || "20");
    const scrollHeight = div.scrollHeight;

    document.body.removeChild(div);

    return Math.round(scrollHeight / lineHeight);
  }

  const initialValues = useMemo<IValues>(() => {
    return isEdit
      ? {
          title: page?.page.page.title || "",
          folderId: parentFolder?.id || "",
          domainId: 0,
          url: page?.page?.computedUrl || "",
          seoTitle: page?.page.page.seoTitle || "",
          seoDescription: page?.page.page.seoDescription || "",
          seoKeyWords: page?.page.page.seoKeywords || "",
          pageCategoryIds:
            page?.page?.pagePageCategory.length > 0
              ? page?.page?.pagePageCategory.map(
                  (item: PagePageCategoryItem) => item.pageCategory.id
                )
              : [],
          noIndex: page?.page.page.noIndex,
          pageScript: page?.page.page.script,
          watermark: page?.page?.pageWatermark ?? true,
        }
      : {
          title: "",
          folderId: parentFolder?.id || "",
          domainId: 0,
          url: "",
          seoTitle: "",
          seoDescription: "",
          seoKeyWords: "",
          pageCategoryIds: [],
          noIndex: false,
          pageScript: "",
          watermark: true,
        };
  }, [isEdit, page, parentFolder]);

  const trySavePage = async (values: IValues) => {
    const pagePageCategoryInput: GraphPagePageCategoryInput[] =
      values.pageCategoryIds
        .filter(
          (id) =>
            !page?.page?.pagePageCategory
              .map((item: PagePageCategoryItem) => item.pageCategory.id)
              .includes(id)
        )
        .map((item) => ({
          pageCategoryId: item,
        }));

    const removeCategories: GraphPagePageCategoryInput[] =
      removedCategoryIds.map((id) => {
        const pagePageCategoryId = page?.page?.pagePageCategory.find(
          (item: PagePageCategoryItem) => item.pageCategory.id === id
        ).id;
        return { pagePageCategoryId, pageCategoryId: 0 };
      });

    const pageCategoryIds = [...pagePageCategoryInput, ...removeCategories];

    const locationSettings = locationFormRef?.current?.getFormValues();
    const scheduleSettings = scheduleFormRef?.current?.getFormValues();
    const passwordSettings = passwordFormRef?.current?.getFormValues();

    const isValidScheduleAccess = scheduleFormRef?.current?.isFormValid();

    if (scheduleSettings?.scheduleAccess && !isValidScheduleAccess) return;

    const settigsPayload = {
      id: +id,

      ...(locationSettings?.isExactRequired !== undefined && {
        isExactRequired: locationSettings.isExactRequired,
      }),

      ...(locationSettings?.isExactCheck !== undefined && {
        isExactCheck: locationSettings.isExactCheck,
      }),

      ...(locationSettings?.locationHint && {
        locationHint: locationSettings.locationHint,
      }),

      checkboxPassword: passwordSettings?.checkboxPassword,
      ...(passwordSettings && {
        password: passwordSettings.password ?? "",
        passwordHint: passwordSettings.passwordHint ?? "",
      }),

      ...(scheduleSettings && {
        scheduleAccessObj: {
          scheduleAccess: scheduleSettings?.scheduleAccess,
          ...(scheduleSettings.timezone && {
            timezone: scheduleSettings.timezone,
          }),

          ...(scheduleSettings.dateStart && {
            dateStart: String(scheduleSettings.dateStart),
          }),

          ...(scheduleSettings.dateEnd && {
            dateEnd: String(scheduleSettings.dateEnd),
          }),

          ...(scheduleSettings.showNothing !== undefined && {
            showNothing: scheduleSettings.showNothing,
          }),

          ...(scheduleSettings.link && {
            link: scheduleSettings.link,
          }),

          ...(scheduleSettings.file && {
            file: scheduleSettings.file,
          }),

          ...(scheduleSettings.fileName && {
            fileName: scheduleSettings.fileName,
          }),

          ...(scheduleSettings.isFilesChanged && {
            isFilesChanged: scheduleSettings.isFilesChanged,
          }),

          schedules: scheduleSettings.schedules,
        },
      }),

      ...(locationSettings && {
        locationObjects: {
          ...(locationSettings.isExactCheck !== undefined && {
            isExactCheck: !!locationSettings.isExactCheck,
          }),

          ...(locationSettings.isExactRequired !== undefined && {
            isExactRequired: !!locationSettings.isExactRequired,
          }),

          ...(locationSettings.locationHint && {
            hint: locationSettings.locationHint,
          }),

          ...(locationSettings.locationItems && {
            locationItems: locationSettings.locationItems,
          }),
        },
      }),
    };

    const pagePayload = {
      ...values,
      domainId: !shared ? +values.domainId : folderId ? null : +projectId,
      folderId: !shared
        ? values?.folderId
          ? +values?.folderId
          : null
        : +folderId,
      seoTitle: values.seoTitle,
      seoDescription: values.seoDescription,
      seoKeyWords: values.seoKeyWords,
      noIndex: values.noIndex,
      pageScript: values.pageScript,
      pageCategoryIds,
    };

    if (settigsPayload?.id) {
      try {
        await updateSettings({
          variables: {
            ...settigsPayload,
          },
        });
      } catch (error) {
        console.error(error);
      }
    }

    if (isEdit) {
      try {
        const { data } = await updatePage({
          variables: {
            ...pagePayload,
            id: +id,
          },
        });
        if (data?.updatePage) {
          isHaveBeenSaved(true);
          navigate(
            pagePayload.folderId
              ? `${lng}/projects/${projectId}/folder/${pagePayload.folderId}`
              : `${lng}/projects/${projectId}`
          );
        }
      } catch (error) {
        if (error.message === Error.UNIQUE_URL) {
          setShowUrlError(true);
        }
      }
    } else {
      if (user) {
        if (
          user?.countMyPages >= FREE_LIMIT.PAGE &&
          !userData?.user.admin &&
          !isPremium
        ) {
          isUpgradeShow(true);
          return;
        }
      }

      try {
        const { data } = await createPage({
          variables: { ...pagePayload, type: +type },
        });
        if (data?.pageNew?.id) {
          isShowTemplateModal({
            isActive: true,
            page: {
              ...data?.pageNew,
              type: +type,
              isCreated: true,
              pageId: data.pageNew.id,
              folderId: data.pageNew?.folder?.id || null,
            },
          });
        }
      } catch (error) {
        if (error.message === Error.UNIQUE_URL) {
          setShowUrlError(true);
        }
      }
    }
  };

  useEffect(() => {
    if (page) {
      setTimeout(() => {
        refetch();
      }, 0);
    }
  }, [page, refetch]);

  useEffect(() => {
    if ((projectId || page?.page?.domain?.id) && formikRef.current) {
      formikRef.current.setFieldValue(
        "domainId",
        projectId || page?.page?.domain?.id
      );
    }
  }, [projectId, page]);

  const backToList = () => {
    navigate(-1);
  };

  if (isEdit && !page) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Spinner animation="border" role="status"></Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-center py-4 bg-white rounded px-3 px-md-4">
        <div className="col-12" style={{ maxWidth: "842px" }}>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={mainSettingsSchema(t)}
            onSubmit={trySavePage}
            enableReinitialize
          >
            {({
              handleSubmit,
              touched,
              errors,
              setFieldValue,
              values,
              setFieldTouched,
            }) => {
              const categoryValues = values.pageCategoryIds.map((categoryId) =>
                categoryOptions.find((item) => item.value === categoryId)
              );

              const handleCategoryChange = (
                selectedOptions: MultiValue<SelectOption>
              ) => {
                const newValues = selectedOptions
                  ? selectedOptions.map((item) => item.value)
                  : [];

                const validIds =
                  page?.page?.pagePageCategory.map(
                    (item: PagePageCategoryItem) => item.pageCategory.id
                  ) || [];
                const removed = values.pageCategoryIds.filter(
                  (id) => !newValues.includes(id) && validIds.includes(id)
                );
                if (removed.length > 0) {
                  setRemovedCategoryIds((prev) => [
                    ...prev,
                    ...removed.filter((id: number) => !prev.includes(id)),
                  ]);
                }
                setFieldValue("pageCategoryIds", newValues);
              };

              return (
                <Form>
                  <Tab.Container
                    activeKey={activeForm}
                    onSelect={(k) => setActiveForm(k!)}
                  >
                    <Nav
                      variant="tabs"
                      className="w-100 mb-4 p-0 d-flex justify-content-between"
                    >
                      {SETTINGS_TABS.map(
                        ({ key, text, mobileText, isPremiumTab }) => (
                          <Nav.Item
                            key={key}
                            className={`flex-md-fill d-flex justify-content-center ${activeForm === key && "tab-active-border"}`}
                          >
                            <Nav.Link
                              eventKey={key}
                              className="d-flex border-0 d-flex align-items-center px-2 px-md-3 bg-transparent text-dark fw-bold"
                            >
                              <p className="d-md-block d-none m-0 fs-5 text-nowrap">
                                {text}
                              </p>
                              <p className="d-md-none d-block m-0 fs-7">
                                {mobileText || text}
                              </p>
                              {isPremiumTab && !isPremiumUser && (
                                <PopoverBtn
                                  text={
                                    <>
                                      {t("page.Want to unlock?")}{" "}
                                      <Link
                                        to={`${lng}/payment`}
                                        className="text-white text-decoration-none"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {t("page.Go Premium")}
                                      </Link>
                                    </>
                                  }
                                  placement="bottom"
                                  tooltipWidth={130}
                                  icon={
                                    <img
                                      className="ms-2"
                                      src={crown_img}
                                      alt="crown"
                                    />
                                  }
                                />
                              )}
                            </Nav.Link>
                          </Nav.Item>
                        )
                      )}
                    </Nav>
                    <Tab.Content className="p-0 mb-4 mb-md-5">
                      <Tab.Pane eventKey={ACTIVE_FORM.main}>
                        <div className="form-floating mb-3">
                          <StyledInput
                            name="title"
                            label={t("page.pageName")}
                            error={!!(touched.title && errors.title)}
                            className="pe-5"
                            tooltip={{
                              text: t(
                                "page.Used to identify your page within the project. This name is for internal use only."
                              ),
                              className: "tooltip-icon position-absolute",
                            }}
                          />
                        </div>
                        <div className="form-floating mb-3">
                          <StyledInput
                            name="url"
                            label={t("page.pageUrl")}
                            onChange={(e: BaseSyntheticEvent) => {
                              setShowUrlError(false);
                              isUrlUniqueError(false);
                              setFieldValue("url", e.target.value);
                            }}
                            className="pe-5"
                            error={
                              !!(
                                (touched.url && errors.url) ||
                                showUrlError ||
                                isUrlError
                              )
                            }
                            tooltip={{
                              text: t(
                                "page.This will be the page's public web address. Use only lowercase letters, numbers, and hyphens."
                              ),
                              className: "tooltip-icon position-absolute",
                            }}
                          />
                          {(showUrlError || isUrlError) && !errors.url && (
                            <p className="mt-1 mb-0 fs-7 text-danger">
                              {t("page.uniqueUrl")}
                            </p>
                          )}
                        </div>
                        {!shared || hasAccess ? (
                          <>
                            <Field name="domainId">
                              {() => (
                                <ReactSelect
                                  className="mb-3"
                                  options={domainOptions}
                                  value={domainOptions.find(
                                    (domain) =>
                                      domain.value === +values.domainId
                                  )}
                                  onChange={(option: SelectOption) => {
                                    setFieldValue("domainId", option.value);
                                    setProjectId(option.value);
                                  }}
                                  placeholder="Select Project"
                                  isLoading={userLoading || accessLoading}
                                  label={t("page.project")}
                                />
                              )}
                            </Field>
                            <Field name="folderId">
                              {() => (
                                <ReactSelect
                                  className="mb-3 position-relative"
                                  options={folderOptions}
                                  value={folderOptions?.find(
                                    (domain) =>
                                      domain.value === +values.folderId
                                  )}
                                  onChange={(option: SelectOption) =>
                                    setFieldValue("folderId", option.value)
                                  }
                                  placeholder="Select Folder"
                                  isLoading={userLoading}
                                  isDisabled={!folders?.length}
                                  label={t("page.folder")}
                                />
                              )}
                            </Field>
                            {userData?.user.admin && (
                              <Field name="pageCategoryIds">
                                {() => (
                                  <ReactSelect
                                    className="mb-3"
                                    options={categoryOptions}
                                    value={categoryValues}
                                    onChange={handleCategoryChange}
                                    isMulti
                                    placeholder="Select Categories"
                                    isLoading={pageCategoryLoading}
                                    label="Categories"
                                  />
                                )}
                              </Field>
                            )}
                          </>
                        ) : null}
                        <Form.Group className="d-flex flex-column flex-sm-row gap-2 gap-sm-0 align-items-sm-center">
                          <div className="d-flex align-items-center">
                            <Field name="watermark">
                              {({ field }: FieldProps) => (
                                <Form.Check
                                  type="switch"
                                  id="watermark"
                                  {...field}
                                  checked={!!field.value}
                                  disabled={
                                    !userData?.user?.admin &&
                                    !userData?.user?.activePremium
                                  }
                                  onChange={(e) =>
                                    setFieldValue("watermark", e.target.checked)
                                  }
                                />
                              )}
                            </Field>
                            <Form.Label
                              htmlFor="watermark"
                              className="mb-0 fs-7 text-dark-gray text-nowrap"
                            >
                              {t("page.watermarkTitle")}
                            </Form.Label>
                          </div>
                          {!userData?.user?.admin &&
                            !userData?.user?.activePremium && (
                              <div
                                className="d-flex align-items-center bg-primary-info rounded-2 ms-sm-3"
                                style={{ padding: "6px 12px" }}
                              >
                                <InfoCircle className="fs-7 text-primary me-2" />
                                <p className="text-primary fs-7 m-0">
                                  {t("page.watermarkTooltip")}{" "}
                                  <Link
                                    to={`${lng}/payment`}
                                    className="text-decoration-underline"
                                  >
                                    {t("payment.Premium Plan")}
                                  </Link>
                                </p>
                              </div>
                            )}
                        </Form.Group>
                      </Tab.Pane>
                      <Tab.Pane eventKey={ACTIVE_FORM.restrictions}>
                        <RestrictionsTab
                          pageData={page}
                          locationFormRef={locationFormRef}
                          scheduleFormRef={scheduleFormRef}
                          passwordFormRef={passwordFormRef}
                        />
                      </Tab.Pane>
                      <Tab.Pane eventKey={ACTIVE_FORM.seo}>
                        <div className="form-floating mb-3">
                          <StyledInput
                            name="seoTitle"
                            label={t("page.seoTitle")}
                            disabled={!isPremiumUser}
                            onChange={(e: BaseSyntheticEvent) => {
                              setFieldTouched("seoTitle", true);
                              setFieldValue("seoTitle", e.target.value);
                            }}
                            error={!!(touched.seoTitle && errors.seoTitle)}
                            className="pe-5"
                            tooltip={{
                              text: t("page.seoTitleInfo"),
                              className: "tooltip-icon position-absolute",
                            }}
                          />
                          {(showUrlError || isUrlError) && !errors.url && (
                            <p className="mt-1 mb-0 fs-7 text-danger">
                              {t("page.uniqueUrl")}
                            </p>
                          )}
                          <div className="text-gray text-end fs-7">
                            {values.seoTitle?.length || 0}/{LENGTH.TITLE_SEO}
                          </div>
                        </div>
                        <div className="form-floating mb-3">
                          <StyledInput
                            className="pe-5"
                            disabled={!isPremiumUser}
                            labelClassName={`seoDescriptionLabel pb-0 pt-2 mt-2 ${values.seoDescription?.length && "activeLabel"}`}
                            name="seoDescription"
                            label={t("page.seoDescription")}
                            onChange={(e: BaseSyntheticEvent) => {
                              setFieldTouched("seoDescription", true);
                              setFieldValue("seoDescription", e.target.value);
                            }}
                            as="textarea"
                            placeholder={t("page.seoDescription")}
                            error={
                              !!(
                                touched.seoDescription && errors.seoDescription
                              )
                            }
                            tooltip={{
                              text: t("page.seoDescrInfo"),
                              className: "tooltip-icon position-absolute",
                            }}
                            style={{ resize: "none", height: "100px" }}
                          />
                          {(showUrlError || isUrlError) && !errors.url && (
                            <p className="mt-1 mb-0 fs-7 text-danger">
                              {t("page.uniqueUrl")}
                            </p>
                          )}
                          <div className="text-gray text-end fs-7">
                            {values.seoDescription?.length || 0}/
                            {LENGTH.SEO_DESCRIPTION}
                          </div>
                        </div>
                        <div className="form-floating mb-3">
                          <StyledInput
                            name="seoKeyWords"
                            disabled={!isPremiumUser}
                            label={t("page.seoKeywords")}
                            onChange={(e: BaseSyntheticEvent) => {
                              setFieldTouched("seoKeyWords", true);
                              setFieldValue("seoKeyWords", e.target.value);
                            }}
                            className="pe-5"
                            error={
                              !!(touched.seoKeyWords && errors.seoKeyWords)
                            }
                            tooltip={{
                              text: t("page.seoKeyInfo"),
                              className: "tooltip-icon position-absolute",
                            }}
                          />
                          {(showUrlError || isUrlError) && !errors.url && (
                            <p className="mt-1 mb-0 fs-7 text-danger">
                              {t("page.uniqueUrl")}
                            </p>
                          )}
                          <div className="text-gray text-end fs-7">
                            {values.seoKeyWords?.length || 0}/{LENGTH.KEY}
                          </div>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                          <input
                            id="noIndex"
                            disabled={!isPremiumUser}
                            name="noIndex"
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={values.noIndex}
                            onChange={(e) =>
                              setFieldValue("noIndex", e.target.checked)
                            }
                          />
                          <label
                            htmlFor="noIndex"
                            className="form-label fs-7 text-dark ps-2 m-0 me-2"
                          >
                            Noindex
                          </label>
                          <PopoverBtn
                            className="tooltip-icon mb-1"
                            text={t("page.indexTooltip")}
                            error={false}
                          />
                        </div>
                      </Tab.Pane>
                      <Tab.Pane eventKey={ACTIVE_FORM.script}>
                        <div className="form-floating codeField">
                          <StyledInput
                            className="pe-5 save-translations position-relative text-dark"
                            labelClassName={`seoDescriptionLabel pb-0 pt-2 mt-2`}
                            name="pageScript"
                            label={t("page.Write a script for your page")}
                            onChange={(e: BaseSyntheticEvent) => {
                              const count = e.target?.value?.split("\n").length;

                              setFieldTouched("pageScript", true);
                              setFieldValue("pageScript", e.target.value);
                              setLines(
                                Array.from({ length: count }, (_, i) => i + 1)
                              );
                            }}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            as="textarea"
                            placeholder={t("page.Write a script for your page")}
                            error={!!(touched.pageScript && errors.pageScript)}
                            tooltip={{
                              text: t("page.scriptTooltip"),
                              className: "tooltip-icon position-absolute",
                            }}
                            style={{
                              resize: "none",
                              overflow: "hidden",
                              height: `${(document.querySelector(".inputLine") as HTMLElement)?.offsetHeight + 36}px`,
                              minHeight: "226px",
                            }}
                          />
                          {(isFocused || values.pageScript) && (
                            <div className="d-flex flex-column position-absolute inputLine">
                              {lines.map((num, index) => (
                                <p
                                  className="m-0 text-dark"
                                  style={{
                                    height: `${
                                      getWrappedLineCountViaDOM(
                                        values?.pageScript?.split("\n")[index],
                                        (
                                          document.querySelector(
                                            '[name="pageScript"]'
                                          ) as HTMLElement
                                        )?.offsetWidth - 96,
                                        {
                                          fontSize: "16px",
                                        }
                                      ) * 24
                                    }px`,
                                  }}
                                  key={num}
                                >
                                  {num}
                                </p>
                              ))}
                            </div>
                          )}
                          {(showUrlError || isUrlError) && !errors.url && (
                            <p className="mt-1 mb-0 fs-7 text-danger">
                              {t("page.uniqueUrl")}
                            </p>
                          )}
                        </div>
                      </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>
                  <div className="pageSettingsBtns d-flex align-items-center justify-content-between">
                    <button
                      type="button"
                      onClick={backToList}
                      className="white-hover btn btn-outline-dark dark-hover fw-normal text-dark buttomCol col-md-4 me-3 me-md-0 fs-6"
                    >
                      {isEdit ? t("page.backList") : t("profile.cancel")}
                    </button>
                    <button
                      type="button"
                      disabled={
                        (isEdit ? updateLoading : createLoading) ||
                        pageCategoryLoading ||
                        updatingSettingsLoading
                      }
                      onClick={() => handleSubmit()}
                      className="btn btn-primary text-white buttomCol col-md-4 fs-6"
                    >
                      {isEdit ? t("page.save") : t("payment.Create")}
                      {(isEdit ? updateLoading : createLoading) ||
                        (updatingSettingsLoading && (
                          <Spinner
                            animation="border"
                            role="status"
                            className="ms-2"
                          >
                            <span className="visually-hidden">
                              {t("profile.loading")}...
                            </span>
                          </Spinner>
                        ))}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};
